import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { firstValueFrom } from 'rxjs';

import { WindowService } from 'src/shared/providers/window.service';

export interface ImageContent {
  type: 'image';
  source: {
    type: 'base64';
    media_type: 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp';
    data: string; // base64 encoded image data
  };
}

export interface TextContent {
  type: 'text';
  text: string;
}

export type ContentBlock = TextContent | ImageContent;

export interface AutosuggestRequest {
  systemPrompt: string;
  prompt: string | ContentBlock[]; // Support both text-only and multi-modal content
  responseSchema: any;
  maxTokens?: number;
}

export interface AutosuggestResponse {
  result: any;
  timestamp: string;
  debug?: {
    model: string;
    inputTokens: number;
    outputTokens: number;
    latencyMs: number;
    retries: number;
  };
}

/**
 * Generic LLM service for AI suggestions across the app.
 *
 * Uses the Tidy autosuggest API endpoint for structured suggestions with schema validation.
 * Supports both text-only and multi-modal (text + images) requests.
 *
 * Text-only usage:
 * const response = await this.llmService.suggest({
 *   systemPrompt: 'You are a helpful assistant...',
 *   prompt: 'Suggest a subject for this workflow action',
 *   responseSchema: { type: 'object', properties: { suggestion: { type: 'string' } } }
 * });
 *
 * Multi-modal usage (with images):
 * const response = await this.llmService.suggest({
 *   systemPrompt: 'You are a property inspector...',
 *   prompt: [
 *     { type: 'text', text: 'What maintenance issues do you see?' },
 *     { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: '...' } }
 *   ],
 *   responseSchema: { type: 'object', properties: { issues: { type: 'array', ... } } },
 *   maxTokens: 2000
 * });
 */
@Injectable({
  providedIn: 'root'
})
export class LlmService {
  private readonly CHAT_API_BASE = 'https://chat-api.tidy.com';
  private readonly PROXY_PATH = '/chat-api';

  constructor(
    private http: HttpClient,
    private windowService: WindowService
  ) {}

  private get apiUrl(): string {
    // Native (Capacitor) has no dev-server proxy; use real API. Use proxy only in browser on localhost.
    const hostname = this.windowService.hostname;
    const useProxy =
      !Capacitor.isNativePlatform() &&
      (hostname === 'localhost' || hostname === '127.0.0.1');
    const baseUrl = useProxy ? this.PROXY_PATH : this.CHAT_API_BASE;
    return `${baseUrl}/api/autosuggest`;
  }

  /**
   * Get AI suggestions using schema-validated responses
   * @param request AutosuggestRequest with systemPrompt, prompt, and responseSchema
   * @returns Promise with structured result matching the provided schema
   */
  async suggest(request: AutosuggestRequest): Promise<AutosuggestResponse> {
    if (Capacitor.isNativePlatform()) {
      const response = await CapacitorHttp.post({
        url: this.apiUrl,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('tidy_token')}`
        },
        data: request
      });
      return response.data;
    }
    return firstValueFrom(
      this.http.post<AutosuggestResponse>(this.apiUrl, request)
    );
  }
}
